import { ColorKey } from '../../../utilis/types/Types';
import {api} from "../../api.ts";
import {unwrapData} from "../../apiResponse.ts";
import {FetchBaseQueryError} from "@reduxjs/toolkit/query";
import {FetchGroupsResponse, Group, GroupListItem} from "../groups/groupsService.ts";
import {showToast} from "../Notifications/notificationsSlice.tsx";
import {Msisdn} from "../Msisdns/msisdnService.ts";
import moment  from 'moment-timezone';
import {matrixService} from "../../../services/matrixService.ts";
import {BACKEND_PAGE_SIZE, fetchAllPages} from "../../fetchAllPages.ts";

type ExternalIdentifier = {
    id: string;
    name: string;
};

type ExternalAssociationDetails = {
    externalIdentifier?: ExternalIdentifier;
};
export type UserRole = "SUPERADMIN" | "ADMIN" | "USER";

export interface User {
    id: number;
    userId: number;
    email: string;
    firstName: string;
    lastName: string;
    status: string;
    iconColor: ColorKey | undefined;
    createdAt: string;
    position?: string;
    avatarUrl?: string | undefined;
    chatInitializedAt: string | null;
}
export interface UserElement {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    status: string;
    iconColor: ColorKey | null;
    createdAt: string;
    position?: string;
    avatarUrl?: string | undefined;
    userTimeZone?: string;
    userId?: number;
    chatInitializedAt: string | null;
}

type CustomUserStatus = {
    userId: number;
    name: string;
    icon: string;
    dueDate: string;
};

type MeetingUserStatus = {
    status: string;
    dateFrom: string;
    dateTo: string;
};

type TelcoUserStatus = {
    status: string;
    lastUpdated: string | null;
};

type DoNotDisturbUserStatus = {
    userId: number;
    enabled: boolean;
    lastUpdatedAt: string;
};

export type UserStatus = {
    userId: number;
    position?: string;
    customUserStatus: CustomUserStatus;
    telcoUserStatus: TelcoUserStatus;
    doNotDisturbUserStatus: DoNotDisturbUserStatus;
    meetingUserStatus: MeetingUserStatus;
};

export interface UserListItem {
    id: number;
    userId: number;
    firstName: string;
    lastName: string;
    role?: UserRole;
    position?: string;
    groups?: Group[];
    phoneNumbers: Msisdn[];
    status: string;
    avatarUrl: string;
    email: string;
    iconColor: ColorKey;
    createdAt: string;
}

export type UserFormData = {
    userId: number;
    firstName: string;
    lastName: string;
    role?: UserRole;
    position?: string;
    groups?: GroupListItem[];
    phoneNumbers: Msisdn[];
    email: string;
    iconColor: ColorKey | undefined;
    createdAt: string;
    avatarUrl?: string;
    externalAssociationDetails?: ExternalAssociationDetails;
    status?: UserStatus;
};

export interface UsersQueryParams {
    phoneNumbers?: string[];
    groups?: string[];
    roles?: string[];
    status?: string[];
    phrase?: string;
    order?: 'ASC' | 'DESC';
}
export interface MissedCallData {
    phoneNumber: number;
    totalDndLastMissedCalls: number;
}
export const usersApi = api.injectEndpoints({
    endpoints: (builder) => ({
        // Admin endpoints
        getUsers: builder.query<{ data: UserListItem[] }, void>({
            queryFn: async (_arg, _queryApi, _extraOptions, baseQuery) => {
                const result = await fetchAllPages<UserListItem>(
                    baseQuery,
                    ({limit, offset}) => ({url: `/api/admin/v1/users`, method: 'GET', params: {limit, offset}}),
                    (data) => (data as {data?: UserListItem[]})?.data ?? [],
                );
                if ('error' in result) {
                    return {error: result.error};
                }
                return {data: {data: result.items}};
            },
            providesTags: ['Users'],
        }),
        getAdminUsersList: builder.query<{ users: User[], totalCount: number }, UsersQueryParams | void>({
            queryFn: async (usersQuery, _queryApi, _extraOptions, baseQuery) => {
                const queryParams = new URLSearchParams();

                if (usersQuery) {
                    const paramMappings = {
                        phoneNumbers: 'phoneNumberIds',
                        groups: 'groupIds',
                        roles: 'roles',
                        status: 'status',
                        phrase: 'phrase',
                        order: 'order'
                    };

                    Object.entries(paramMappings).forEach(([propName, paramName]) => {
                        const value = usersQuery[propName as keyof typeof usersQuery];

                        if (Array.isArray(value)) {
                            queryParams.append(paramName, value.join(','));
                        } else if (value !== undefined) {
                            queryParams.append(paramName, value.toString());
                        }
                    });
                }

                const usersResult = await fetchAllPages<User>(
                    baseQuery,
                    ({limit, offset}) => {
                        const pageParams = new URLSearchParams(queryParams);
                        pageParams.set('limit', String(limit));
                        pageParams.set('offset', String(offset));
                        return {url: `/api/admin/v1/users?${pageParams.toString()}`, method: 'GET'};
                    },
                    (data) => (data as {data?: User[]})?.data ?? [],
                );

                if ('error' in usersResult) {
                    return { error: usersResult.error };
                }

                return { data: {users: usersResult.items, totalCount: usersResult.totalCount} };
            },
            providesTags: ['Users'],
        }),
        getUserStatusesChunked: builder.query<UserStatus[], number[]>({
            queryFn: async (userIds, _queryApi, _extraOptions, baseQuery) => {
                const chunks: number[][] = [];
                for (let index = 0; index < userIds.length; index += BACKEND_PAGE_SIZE) {
                    chunks.push(userIds.slice(index, index + BACKEND_PAGE_SIZE));
                }

                const results = await Promise.all(
                    chunks.map((chunk) => baseQuery({
                        url: '/api/v1/statuses',
                        params: { users: chunk.join(',') }
                    }))
                );

                const failed = results.find((result) => result.error);
                if (failed?.error) {
                    return { error: failed.error as FetchBaseQueryError };
                }

                return {
                    data: results.flatMap(
                        (result) => (result.data as { statuses?: UserStatus[] })?.statuses ?? []
                    ),
                };
            },
            providesTags: ['Statuses'],
        }),
        getGroupForUser: builder.query({
            query: ({userId}) => {
                return {
                    url: `/api/admin/v1/groups/${userId}`,
                    params: { userId },
                    method: 'GET',
                };
            },
            transformResponse: (data:FetchGroupsResponse[] ) => {

                return data.map((item) => item.group);
            },
            providesTags: (_, __, arg) => [{ type: 'Group',id: arg.userId }],
        }),
        getPhoneNumbersForUser: builder.query({
            query: ({userId}) => {
                return {
                    url: `/api/admin/v1/phone-numbers`,
                    params: { userId },
                    method: 'GET',
                };
            },
            transformResponse: (response: { data: Msisdn[] }) => unwrapData(response),
            providesTags: (_, __, arg) => [{ type: 'PhoneNumbers',id: arg.userId }],
        }),
        getUserById: builder.query<any, { id?: string }>({
            query: ({id}) => `/api/admin/v1/users/${id}`,
            transformResponse: (response: { data: any }) => unwrapData(response),
            providesTags: (_, __, arg) => [{ type: 'User', id: arg.id }],
        }),
        deleteUser: builder.mutation({
            query: ({id}) => ({
                url: `/api/admin/v1/users/${id}`,
                method: 'DELETE',
            }),
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                    dispatch(showToast({description: 'User deactivated successfully!', type: 'success'}));
                } catch {
                    dispatch(showToast({description: 'Failed to deactivate user!', type: 'error'}));
                }
            },
            invalidatesTags: (_, __, arg) => [
                'Users',
                { type: 'User', id: arg.id }
            ],
        }),
        enableUser: builder.mutation({
            query: ({id}) => ({
                url: `/api/admin/v1/users/${id}`,
                method: 'POST',
            }),
            invalidatesTags: (_, __, arg) => [
                'Users',
                { type: 'User', id: arg.id }
            ],
        }),
        createUser: builder.mutation({
            query: ({userData}) => ({
                url: `/api/admin/v1/users`,
                method: 'POST',
                body: userData,
            }),
            transformResponse: (response: { data: { userId: number } }) => unwrapData(response),
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                    dispatch(showToast({description: 'User created successfully!', type: 'success'}));
                } catch (error) {
                    const err = error as { error?: { data?: { message?: string } } };

                    if(err?.error?.data?.message) {
                        if(err.error.data.message.includes('already exists')) {
                            dispatch(showToast({description: 'The email address you entered is already associated with an existing account.', type: 'error'}));
                        return
                        }
                        dispatch(showToast({description: err.error.data.message, type: 'error'}));
                        return;
                    }
                    dispatch(showToast({description: 'Failed to create user!', type: 'error'}));
                }
            },
            invalidatesTags: ['Users'],
        }),
        updateUser: builder.mutation({
            query: ({userData, userId}) => ({
                url: `/api/admin/v1/users/${userId}`,
                method: 'PUT',
                body: userData,
            }),
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                    dispatch(showToast({description: 'User updated successfully!', type: 'success'}));
                } catch {
                    dispatch(showToast({description: 'Failed to update user!', type: 'error'}));
                }
            },
            invalidatesTags: (_, __, arg) => [
                'Users',
                { type: 'User', id: arg.userId }
            ],
        }),
        assignUserToGroup: builder.mutation({
            query: ({ groupIds, userId }) => ({
                url: `/api/admin/v1/users/${userId}/groups`,
                method: 'PUT',
                body: {groupIds},
            }),
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                } catch {
                    dispatch(showToast({description: 'Failed to assign group!', type: 'error'}));
                }
            },
            invalidatesTags: (_, __, arg) => [
                'Users',
                { type: 'Group', id: arg.userId },
                { type: 'PhoneNumbers', id: arg.userId }
            ],
        }),
        assignUserToPhoneNumber: builder.mutation({
            query: ({ phoneNumberIds, userId }) => ({
                url: `api/admin/v1/users/${userId}/phone-numbers`,
                method: 'PUT',
                body: {phoneNumberIds},
            }),
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                } catch {
                    dispatch(showToast({description: 'Failed to assign phone number!', type: 'error'}));
                }
            },
            invalidatesTags: (_, __, arg) => [
                'Users',
                { type: 'PhoneNumbers', id: arg.userId }
            ],
        }),

            fetchUsers: builder.query<UserElement[], {phrase?: string}>({
            queryFn: async ({phrase}, _queryApi, _extraOptions, baseQuery) => {
                const result = await fetchAllPages<UserElement>(
                    baseQuery,
                    ({limit, offset}) => ({
                        url: '/api/v1/users?technical=false&sortBy=FIRST_NAME&order=ASC',
                        params: {phrase, limit, offset},
                    }),
                    (data) => unwrapData(data as { data: UserElement[] }) ?? [],
                );
                if ('error' in result) {
                    return {error: result.error};
                }
                return {data: result.items};
            },
            providesTags: [{ type: 'User', id: 'LIST' }],
        }),
        getRegularUserById: builder.query({
            query: ({id}) => `/api/admin/v1/users/${id}`,
            transformResponse: (response: { data: User }) => unwrapData(response),
            providesTags: (_, __, arg) => [{ type: 'User', id: arg.id }],
        }),
        fetchCurrentUser: builder.query<User, string | null>({
            queryFn: async (userId, _queryApi, _extraOptions, baseQuery) => {
                if (!userId) throw new Error('User ID not found');

                const userResult = await baseQuery({
                    url: '/api/v1/users',
                    params: {userId},
                    method: 'GET',
                });

                if (!userResult.error) {
                    const users = unwrapData(userResult.data as { data: User[] } | User[]);
                    const user = users[0];

                    if (user) {
                        return {data: {...user, userId: user.userId ?? user.id}};
                    }
                }

                const adminResult = await baseQuery({
                    url: `/api/admin/v1/users/${userId}`,
                    method: 'GET',
                });

                if (adminResult.error) {
                    return {error: adminResult.error as FetchBaseQueryError};
                }

                const user = unwrapData(adminResult.data as { data: User } | User);

                return {
                    data: {...user, userId: user.userId ?? user.id},
                };
            },
            providesTags: (_result, _error, arg) => [{ type: 'User', id: Number(arg) }],
        }),
        createRegularUser: builder.mutation<User, Partial<User>>({
            query: (newUser) => ({
                url: '/api/admin/v1/users',
                method: 'POST',
                body: newUser,
            }),
            transformResponse: (response: { data: User }) => unwrapData(response),
            invalidatesTags: [{ type: 'User', id: 'LIST' }],
        }),
        updateRegularUser: builder.mutation<User, User>({
            query: (updatedUser) => ({
                url: `/api/admin/v1/users/${updatedUser.userId}`,
                method: 'PUT',
                body: updatedUser,
            }),
            transformResponse: (response: { data: User }) => unwrapData(response),
            invalidatesTags: (_result,_error, arg) => [{ type: 'User', id: Number(arg.userId) }],
        }),
        deleteRegularUser: builder.mutation<void, number>({
            query: (userId) => ({
                url: `/api/admin/v1/users/${userId}`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'User', id: 'LIST' }],
        }),
        searchUsers: builder.query<User[], string>({
            query: (phrase) => ({
                url: '/api/v1/users?technical=false&sortBy=FIRST_NAME&order=ASC',
                params: { phrase },
            }),
            transformResponse: (response: { data: User[] }) =>
                unwrapData(response).map((user) => ({ ...user, userId: user.id })),
        }),
        toggleDnd: builder.mutation<any, {dueDate?: string | Date | null, enabled:boolean, userId: string | null }>({
            query: ({enabled, userId, dueDate}) => {
                if (!userId) throw new Error('User ID not found');
                return {
                    url: `/api/v1/statuses/user/${userId}/dnd`,
                    method: 'PATCH',
                    body: { enabled, dueDate },
                };
            },
            invalidatesTags: [{type: 'Statuses' }],
        }),
        getMissedCallsPerUser: builder.query<{ data: MissedCallData[] }, number>({
            query: (userId) => ({
                url: `/api/v1/users/${userId}/missed-calls`,
                method: 'GET',
            }),
            providesTags: ['DndMissedCalls', {type: 'Calls', id: 'MISSED'}],
        }),
        resetDndNotification: builder.mutation<any, {userId: any, phoneNumberId: number }>({
            query: ({userId, phoneNumberId}) => {
                if (!userId) throw new Error('User ID not found');
                return {
                    url: `/api/v1/users/${userId}/phone-numbers/${phoneNumberId}/settings`,
                    method: 'PUT',
                    body: { dndNotificationResetAt:  moment(new Date().toISOString()).format("YYYY-MM-DDTHH:mm:ss.SSSZ") },
                };
            },
            invalidatesTags: [{ type: 'DndMissedCalls' }],
        }),
        markChatInitialized: builder.mutation<void, { userId: string | number }>({
            query: () => ({
                url: `/api/v1/chats/initialized`,
                method: 'POST',
            }),
            invalidatesTags: (_, __, arg) => [{ type: 'User', id: Number(arg.userId) }],
        }),
        resolveMatrixIds: builder.query<Record<string, string>, { matrixIds: string[] }>({
            query: ({ matrixIds }) => {
                if (!matrixIds || matrixIds.length === 0) {
                    throw new Error('Matrix IDs are required');
                }
                return {
                    url: `/api/v1/users/resolve-mxids`,
                    method: 'POST',
                    body: { mxids: matrixIds },
                };
            },
            transformResponse: (response: { users: Record<string, number> } | Record<string, number>, _meta, arg) => {
                const result: Record<string, string> = {};

                const usersMap: Record<string, number> = 'users' in response && typeof response.users === 'object'
                    ? response.users
                    : (response as Record<string, number>);

                arg.matrixIds.forEach(fullMatrixId => {
                    const match = fullMatrixId.match(/^@([^:]+):/);
                    if (match) {
                        const localPart = match[1];
                        result[fullMatrixId] = usersMap[localPart] !== undefined
                            ? String(usersMap[localPart])
                            : '';
                    }
                });

                return result;
            }
        }),
        resolveUserIds: builder.query<Record<string, string>, { userIds: string[] }>({
            query: ({ userIds }) => {
                if (!userIds || userIds.length === 0) {
                    throw new Error('User IDs are required');
                }
                return {
                    url: `/api/v1/users/resolve-userids`,
                    method: 'POST',
                    body: { userIds: userIds },
                };
            },
            transformResponse: (response: { users: Record<number, string> }) => {
                const result: Record<string, string> = {};
                const homeServerURL = matrixService.getClient()?.getDomain()
                Object.entries(response.users).forEach(([userId, matrixId]) => {
                    result[String(userId)] = `@${matrixId}:${homeServerURL}`;
                });

                return result;
            }
        }),
    }),
});

export const {
    // Admin hooks
    useGetUsersQuery,
    useGetAdminUsersListQuery,
    useGetUserStatusesChunkedQuery,
    useGetUserByIdQuery,
    useGetGroupForUserQuery,
    useGetPhoneNumbersForUserQuery,
    useUpdateUserMutation,
    useDeleteUserMutation,
    useCreateUserMutation,
    useAssignUserToGroupMutation,
    useAssignUserToPhoneNumberMutation,
    useEnableUserMutation,

    // Regular user hooks
    useFetchUsersQuery,
    useFetchCurrentUserQuery,
    useCreateRegularUserMutation,
    useUpdateRegularUserMutation,
    useDeleteRegularUserMutation,
    useToggleDndMutation,
    useSearchUsersQuery,
    useGetMissedCallsPerUserQuery,
    useResetDndNotificationMutation,

    useLazyGetRegularUserByIdQuery,
    useLazyResolveMatrixIdsQuery,
    useLazyResolveUserIdsQuery,
    useMarkChatInitializedMutation,
} = usersApi;
